/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useRef, useState } from "react";
import { useTheme } from "../../theme/ThemeContext";
import envConfig from "../../config/envConfig"; // Holds fileServerUrl and fileServerApiKey
import { retrieve } from "../../utils/storageHelper"; // For getting organization/user info from storage

/*
 * Usage Example:
 * <FileUploadInput
 *   is_public={true}
 *   value={form.banner_image}
 *   onChange={url => setForm(f => ({ ...f, banner_image: url }))}
 *   allowedFileTypes={["image"]}
 *   expectedImageWidth={1200}
 *   expectedImageHeight={400}
 *   inputProps={{ name: "banner_image" }}
 *   hint="Only .png, .jpg. Max 1 MB. Size must be exactly 1200x400."
 * />
 */

// --- File type mapping & validation setup ---
const FILE_TYPE_MAP = {
    image: {
        label: "Image",
        accept: "image/*",
        maxSize: 1 * 1024 * 1024,
        validate: (file) => file.type.startsWith("image/"),
    },
    video: {
        label: "Video",
        accept: "video/*",
        maxSize: 5 * 1024 * 1024,
        validate: (file) => file.type.startsWith("video/"),
    },
    audio: {
        label: "Audio",
        accept: "audio/*",
        maxSize: 2 * 1024 * 1024,
        validate: (file) => file.type.startsWith("audio/"),
    },
    text: {
        label: "Text",
        accept: ".txt,.csv,.log,.json,.xml,.yaml,.md",
        maxSize: 0.5 * 1024 * 1024,
        validate: (file) =>
            file.type.startsWith("text/") ||
            [".txt", ".csv", ".log", ".json", ".xml", ".yaml", ".md"].some(ext =>
                file.name.endsWith(ext)
            ),
    },
    document: {
        label: "Document",
        accept: ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp",
        maxSize: 2 * 1024 * 1024,
        validate: (file) =>
            [
                "application/pdf",
                "application/msword",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                "application/vnd.ms-excel",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                "application/vnd.ms-powerpoint",
                "application/vnd.openxmlformats-officedocument.presentationml.presentation",
            ].includes(file.type) ||
            [
                ".pdf",
                ".doc",
                ".docx",
                ".xls",
                ".xlsx",
                ".ppt",
                ".pptx",
                ".odt",
                ".ods",
                ".odp",
            ].some(ext => file.name.endsWith(ext)),
    },
    archive: {
        label: "Archive",
        accept: ".zip,.rar,.tar,.gz,.tgz,.7z,.bz2",
        maxSize: 4 * 1024 * 1024,
        validate: (file) =>
            [".zip", ".rar", ".tar", ".gz", ".tgz", ".7z", ".bz2"].some(ext =>
                file.name.endsWith(ext)
            ),
    },
    other: {
        label: "Other",
        accept: "*",
        maxSize: 1 * 1024 * 1024,
        validate: () => true,
    },
};

// --- Simple SVG Upload Icon ---
function UploadIcon({ size = 22, color = "#2d6cf6" }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
            <title>Upload</title>
            <rect x="5" y="16" width="14" height="3" rx="1.5" fill={color} opacity={0.7} />
            <rect x="11" y="8" width="2" height="6" rx="1" fill={color} />
            <polygon points="12,4 8,9 10,9 10,13 14,13 14,9 16,9" fill={color} />
        </svg>
    );
}

/**
 * Helper: read an image file and resolve its natural width/height.
 */
function getImageDimensions(file) {
    return new Promise((resolve, reject) => {
        try {
            const reader = new FileReader();
            reader.onload = evt => {
                const img = new Image();
                img.onload = () => {
                    resolve({ width: img.width, height: img.height });
                };
                img.onerror = reject;
                img.src = evt.target.result;
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        } catch (e) {
            reject(e);
        }
    });
}

// --- Main Component ---
export default function FileUploadInput({
    is_public,
    value,
    onChange,
    allowedFileTypes = ["image", "video", "audio", "text", "document", "archive", "other"],
    apiHeaders = {},
    error,             // external error message (optional)
    inputProps = {},   // props passed down to readonly URL input
    uploadTooltip = "Select and upload file",
    hint,              // help/hint text below control
    expectedImageWidth,   // optional: enforce image width
    expectedImageHeight,  // optional: enforce image height
    organizationId,
    applicationId
}) {
    const { theme } = useTheme();
    const fileRef = useRef();

    const [uploading, setUploading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [fileTypeDetected, setFileTypeDetected] = useState(null);
    const [copied, setCopied] = useState(false);

    // Build accept attribute list from allowed file types
    const acceptList = allowedFileTypes
        .map(type => FILE_TYPE_MAP[type])
        .filter(Boolean)
        .map(f => f.accept)
        .join(",");

    /**
     * Validate file type and size.
     * Returns string error or null if OK.
     */
    const basicValidateFile = (file) => {
        for (let type of allowedFileTypes) {
            const typeInfo = FILE_TYPE_MAP[type];
            if (typeInfo.validate(file)) {
                setFileTypeDetected(type);
                if (file.size > typeInfo.maxSize) {
                    return `File too large for ${typeInfo.label} (max ${(typeInfo.maxSize / 1024 / 1024).toFixed(2)} MB).`;
                }
                return null;
            }
        }
        return "Invalid file type.";
    };

    /**
     * Trigger hidden file input click when Upload button is pressed.
     */
    const handleUploadClick = () => {
        if (fileRef.current) fileRef.current.value = "";
        fileRef.current?.click();
    };

    /**
     * Handle file selection:
     * - Basic type/size validation
     * - Optional image width/height validation
     * - Upload to server if all good
     */
    const handleFileChange = async (e) => {
        setErrorMsg("");
        setFileTypeDetected(null);

        const file = e.target.files[0];
        if (!file) return;

        // Step 1: basic validation (type/size)
        const validationError = basicValidateFile(file);
        if (validationError) {
            setErrorMsg(validationError);
            return;
        }

        // Step 2: optional image dimensions validation (only for images)
        // Only run if expected width/height provided AND file is image.
        // SVGs are exempt: they scale to any size, and a viewBox-only SVG
        // reports 0x0 through Image(), which would fail an exact-size check.
        const isSvg = file.type === "image/svg+xml" ||
            file.name.toLowerCase().endsWith(".svg");
        if (
            file.type.startsWith("image/") &&
            !isSvg &&
            (expectedImageWidth || expectedImageHeight)
        ) {
            try {
                const { width, height } = await getImageDimensions(file); // uses FileReader + Image()[web:24][web:30]
                if (
                    (expectedImageWidth && width !== expectedImageWidth) ||
                    (expectedImageHeight && height !== expectedImageHeight)
                ) {
                    const msgParts = [];
                    if (expectedImageWidth && expectedImageHeight) {
                        msgParts.push(`Expected ${expectedImageWidth}x${expectedImageHeight}px`);
                    } else if (expectedImageWidth) {
                        msgParts.push(`Expected width ${expectedImageWidth}px`);
                    } else if (expectedImageHeight) {
                        msgParts.push(`Expected height ${expectedImageHeight}px`);
                    }
                    msgParts.push(`but got ${width}x${height}px.`);
                    setErrorMsg(msgParts.join(" "));
                    return;
                }
            } catch (dimErr) {
                setErrorMsg("Could not read image dimensions.");
                return;
            }
        }

        // Step 3: upload
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append("file", file);
            let currentFileType = "other";
            for (let t of allowedFileTypes) {
                if (FILE_TYPE_MAP[t] && FILE_TYPE_MAP[t].validate(file)) {
                    currentFileType = t; break;
                }
            }
            formData.append("file_type", currentFileType);
            if (is_public) formData.append("public", "true");

            // Fill dynamic headers
            const headers = { ...apiHeaders };
            headers["X-API-KEY"] = envConfig.fileServerApiKey;
            const userData = retrieve("userData");
            const resolvedOrgId = (organizationId !== undefined && organizationId !== null && organizationId !== "")
                ? organizationId
                : (userData && userData.organization_id ? userData.organization_id : 0);
            headers["X-ORG-ID"] = resolvedOrgId;

            if (applicationId !== undefined && applicationId !== null && applicationId !== "") {
                headers["X-APP-ID"] = applicationId;
            }

            const uploadUrl = envConfig.fileServerUrl.replace(/\/$/, "") + "/upload";

            const response = await fetch(uploadUrl, {
                method: "POST",
                headers,
                body: formData,
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || "Upload failed");
            }

            let fullUrl = "";
            const fileId = data.file_id || (data.public_url ? data.public_url.split('/').pop().split('?')[0] : "");
            
            // For images with a public URL, use the public Flask URL so <img> tags render properly
            if ((fileTypeDetected === "image" || file.type.startsWith("image/")) && data.public_url) {
                fullUrl = envConfig.fileServerUrl.replace(/\/$/, "") + data.public_url;
            } else {
                // For non-images (PDFs, documents) OR private images, use the OAuth-protected Django proxy
                if (fileId) {
                    const apiBase = envConfig.backendApiUrl ? envConfig.backendApiUrl.replace(/\/$/, "") : "";
                    fullUrl = `${apiBase}/arcee/application/file/download/${fileId}/`;
                    fullUrl += `?filename=${encodeURIComponent(file.name)}`;
                } else {
                    fullUrl = "";
                }
            }

            if (onChange) onChange(fullUrl, inputProps.name);
        } catch (err) {
            setErrorMsg(err.message || "Upload failed");
        }
        setUploading(false);
    };

    /**
     * Copy URL to clipboard with fallback.
     */
    const handleCopy = async () => {
        if (!value) return;
        try {
            await navigator.clipboard.writeText(value);
        } catch {
            const input = document.createElement("textarea");
            input.value = value;
            document.body.appendChild(input);
            input.select();
            document.execCommand("copy");
            document.body.removeChild(input);
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    /**
     * Clear URL.
     */
    const handleClear = () => {
        if (onChange) onChange("");
    };

    // Final error to show (internal > external)
    const finalError = errorMsg || error;

    return (
        <div style={{ width: "100%" }}>
            {/* Main outlined control (URL + actions) */}
            <div
                style={{
                    border: `1.5px solid ${theme.primary}`,
                    borderRadius: theme.buttonRadius,
                    display: "flex",
                    alignItems: "center",
                    padding: "4px",
                    background: "#fff"
                }}
            >
                {/* Read-only URL */}
                <input
                    {...inputProps}
                    readOnly
                    value={value || ""}
                    style={{
                        border: "none",
                        background: "none",
                        color: "#222",
                        fontFamily: "inherit",
                        width: "100%",
                        padding: "9px 10px",
                        outline: "none",
                        fontSize: "1em"
                    }}
                    className={inputProps.className}
                />

                {/* Action buttons */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "3px",
                        marginLeft: "6px"
                    }}
                >
                    {/* Upload */}
                    <button
                        style={{
                            border: `1.5px solid ${theme.primary}`,
                            borderRadius: theme.buttonRadius,
                            padding: "2px 7px",
                            background: "none",
                            color: theme.primary,
                            cursor: uploading ? "wait" : "pointer",
                            display: "flex",
                            alignItems: "center"
                        }}
                        disabled={uploading}
                        type="button"
                        title={uploadTooltip}
                        onClick={handleUploadClick}
                    >
                        {uploading ? (
                            <span
                                style={{
                                    display: "inline-block",
                                    width: 22,
                                    height: 22,
                                    border: `3px solid ${theme.primary}33`,
                                    borderTop: `3px solid ${theme.primary}`,
                                    borderRadius: "50%",
                                    animation: "spin 1s linear infinite"
                                }}
                            />
                        ) : (
                            <UploadIcon size={22} color={theme.primary} />
                        )}
                    </button>

                    {/* Copy & Clear */}
                    {value && (
                        <>
                            <button
                                style={{
                                    border: `1.5px solid ${theme.primary}`,
                                    borderRadius: theme.buttonRadius,
                                    padding: "2px 8px",
                                    background: "none",
                                    color: theme.primary,
                                    cursor: "pointer",
                                    position: "relative"
                                }}
                                type="button"
                                title={copied ? "Copied!" : "Copy URL to clipboard"}
                                onClick={handleCopy}
                            >
                                {copied ? "Copied!" : "📋"}
                            </button>
                            <button
                                style={{
                                    border: `1.5px solid ${theme.primary}`,
                                    borderRadius: theme.buttonRadius,
                                    padding: "2px 8px",
                                    background: "none",
                                    color: theme.primary,
                                    cursor: "pointer"
                                }}
                                type="button"
                                title="Clear URL"
                                onClick={handleClear}
                            >
                                ✖
                            </button>
                        </>
                    )}

                    {/* Hidden file input */}
                    <input
                        type="file"
                        ref={fileRef}
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                        accept={acceptList || "*"}
                    />
                </div>

                {/* Local spinner keyframes */}
                <style>
                    {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
                </style>
            </div>

            {/* Hint under control */}
            {hint && (
                <div
                    style={{
                        fontSize: ".96em",
                        color: "#545d6f",
                        margin: "3px 2px 0 5px"
                    }}
                >
                    {hint}
                </div>
            )}

            {/* Error under hint */}
            {finalError && (
                <div
                    style={{
                        color: "#d53b3b",
                        fontSize: "0.92em",
                        marginTop: "4px",
                        marginLeft: "5px"
                    }}
                >
                    {finalError}
                </div>
            )}
        </div>
    );
}
